import PageHeader from "@/components/site/PageHeader";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Check } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import Reveal from "@/components/site/Reveal";
import { SCORECARD_PDF, SCORECARD_FILENAME } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCOVER = [
  "Hidden conversion barriers",
  "Guest experience friction",
  "Revenue opportunities",
  "Operational inefficiencies",
  "Strategic blind spots",
  "Areas most likely limiting growth",
];

const FOR_YOU = [
  "Why bookings feel inconsistent",
  "Why pricing alone does not seem to solve the problem",
  "Why operations feel harder than they should",
  "Why the business is not growing as smoothly as expected",
  "Where the real opportunities are",
];

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function Resources() {
  usePageTitle("Resources");
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name, email, source: "revenue_leak_audit_scorecard" });
      triggerDownload(SCORECARD_PDF, SCORECARD_FILENAME);
      toast.success("Your Revenue Leak Audit Scorecard is downloading now.");
      // Navigate to thank-you page after a short pause so the download initiates
      setTimeout(() => navigate("/thank-you"), 600);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-resources" className="bg-ivory">
      <PageHeader
        overline="Resources"
        title="The Revenue Leak"
        accent="Audit Scorecard."
        description="A strategic self-assessment for short-term rental owners who want to identify hidden issues affecting bookings, profitability, guest experience, and growth."
        testId="resources-header"
      />

      <section className="pb-24 lg:pb-32">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Editorial copy column */}
            <div className="lg:col-span-7 space-y-14 lg:space-y-16">
              <Reveal>
                <div className="space-y-5">
                  <p className="font-serif text-navy text-[1.3rem] md:text-[1.5rem] leading-[1.45]">
                    Most owners focus on occupancy.
                    <br />
                    <em className="font-serif italic text-sage">Experienced operators focus on friction.</em>
                  </p>
                  <p className="font-sans text-charcoal/75 text-[16px] leading-[1.85] max-w-xl">
                    This scorecard is designed to help you identify subtle issues that may be limiting performance
                    across your listing, pricing, guest experience, operations, and long-term growth.
                  </p>
                  <p className="font-sans text-charcoal/75 text-[16px] leading-[1.85] max-w-xl">
                    It is not about perfection. It is about <em className="font-serif italic text-navy">clarity</em>.
                    Because once you can see what is creating friction, you can make better decisions about what to improve next.
                  </p>
                </div>
              </Reveal>

              <Reveal>
                <div>
                  <p className="overline mb-6">What You'll Discover</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3.5 max-w-2xl">
                    {DISCOVER.map((d, i) => (
                      <li key={i} className="flex gap-3 font-sans text-[15.5px] text-charcoal/80 leading-snug">
                        <Check className="w-4 h-4 text-sage shrink-0 mt-1" strokeWidth={1.8} />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal>
                <div>
                  <p className="overline mb-6">Who This Is For</p>
                  <p className="font-sans text-charcoal/75 text-[16px] leading-[1.85] max-w-xl mb-6">
                    This scorecard is designed for short-term rental owners, Airbnb operators, and investors who want
                    a clearer picture of what may be holding their business back.
                  </p>
                  <p className="font-sans text-charcoal/75 text-[15.5px] leading-[1.8] mb-5">It is especially helpful if you have been wondering:</p>
                  <ul className="space-y-2.5 max-w-xl">
                    {FOR_YOU.map((q, i) => (
                      <li key={i} className="flex gap-3 font-sans text-[15.5px] text-charcoal/80 leading-snug">
                        <span className="font-serif italic text-sage shrink-0">·</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal>
                <div>
                  <p className="overline mb-6">How It Works</p>
                  <p className="font-sans text-charcoal/75 text-[16px] leading-[1.85] mb-7 max-w-xl">
                    For each section, rate yourself using:
                  </p>
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-center gap-4">
                      <span className="block w-3 h-3 rounded-full bg-sage shrink-0" />
                      <p className="font-sans text-[15.5px] text-charcoal/85"><span className="font-semibold text-navy">Green</span> — Strong</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="block w-3 h-3 rounded-full bg-gold shrink-0" />
                      <p className="font-sans text-[15.5px] text-charcoal/85"><span className="font-semibold text-navy">Yellow</span> — Needs Attention</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="block w-3 h-3 rounded-full bg-[#B85C5C] shrink-0" />
                      <p className="font-sans text-[15.5px] text-charcoal/85"><span className="font-semibold text-navy">Red</span> — Likely Revenue Leak</p>
                    </div>
                  </div>
                  <p className="font-sans text-charcoal/70 text-[15.5px] leading-[1.8] mt-7 max-w-xl">
                    Then total your results and review the interpretation section at the end.
                  </p>
                </div>
              </Reveal>

              <Reveal>
                <div className="border-l-2 border-gold pl-6 max-w-xl">
                  <p className="overline mb-4">Before You Download</p>
                  <p className="font-sans text-charcoal/75 text-[16px] leading-[1.85]">
                    If this scorecard reveals a few areas that need attention, that is a good thing.
                    Hidden friction is easier to improve once it becomes visible.
                  </p>
                  <p className="font-serif italic text-navy text-[1.1rem] md:text-[1.2rem] leading-[1.55] mt-5">
                    The goal is not to make owners feel behind. The goal is to help them see their business
                    more clearly — so they can focus on the highest-impact opportunities.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Sticky form column */}
            <div className="lg:col-span-5">
              <Reveal>
                <div className="lg:sticky lg:top-32 bg-cream border border-warmGray/20 p-10 lg:p-12" data-testid="scorecard-form-card">
                  <p className="overline mb-5">Free Scorecard</p>
                  <h3 className="font-serif text-navy text-[1.65rem] lg:text-[1.85rem] leading-[1.2] mb-5">
                    The Revenue Leak Audit Scorecard
                  </h3>
                  <p className="font-sans text-[15px] text-charcoal/75 leading-[1.75] mb-9">
                    A strategic self-assessment for short-term rental owners. Identify the subtle friction
                    points limiting bookings, profitability, and growth.
                  </p>
                  <form onSubmit={submit} className="space-y-6" data-testid="scorecard-form">
                    <div>
                      <label className="overline block mb-2" htmlFor="scorecard-name">Your Name (optional)</label>
                      <input
                        id="scorecard-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="First & last"
                        data-testid="scorecard-name"
                        className="input-editorial"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2" htmlFor="scorecard-email">Your Email</label>
                      <input
                        id="scorecard-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        data-testid="scorecard-email"
                        className="input-editorial"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="scorecard-submit"
                      className="btn-gold w-full justify-center disabled:opacity-60"
                    >
                      {loading ? "Sending..." : (<>Download the Scorecard <Download className="w-4 h-4" /></>)}
                    </button>
                  </form>
                  <p className="font-sans text-[13px] text-charcoal/60 mt-6 leading-relaxed">
                    No noise. Just the scorecard, sent calmly.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
